import { dispatchEvent, CustomEventNames } from './eventDispatcher';

/**
 * Tracks the user's active input modality ("keyboard" or "pointer") and
 * broadcasts a single global "InputChange" event when it changes.
 * Used by interactive components to adapt behaviour and accessibility rules.
 * See the carousel controller for a detailed usage example.
 *
 * @example
 * import { subscribeEvent, CustomEventNames } from '@shared/script/eventDispatcher';
 *
 * subscribeEvent(CustomEventNames.InputChange, (event) => {
 *     const { modality } = event;
 *     console.log(`Input modality changed to: ${modality}`);
 * });
 */

type InputModality = 'keyboard' | 'pointer';

let currentModality: InputModality | null = null;

function setModality(next: InputModality, source: string): void {
    if (currentModality === next) {
        return;
    }

    currentModality = next;

    dispatchEvent(CustomEventNames.InputChange, {
        modality: next,
        source,
        ts: Date.now(),
    });
}

function onKeyDown(event: KeyboardEvent): void {
    const navigationKeys = [
        'Tab',
        'ArrowUp',
        'ArrowDown',
        'ArrowLeft',
        'ArrowRight',
    ];

    if (!navigationKeys.includes(event.key)) {
        return;
    }

    setModality('keyboard', 'keydown');
}

function onPointerDown(): void {
    setModality('pointer', 'pointerdown');
}

// Avoid double-initialisation if the module is imported more than once.
if (window.__pepperedInputModalityActive !== true) {
    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('pointerdown', onPointerDown, true);

    window.__pepperedInputModalityActive = true;
}
